import { ref, inject } from 'vue'
import { useClientStore } from '@/stores/ClientStore'
import { useSentry } from '@/vendors/integrations'
import { loadScript } from '@/utils/utils'

const recaptchaSymbol = Symbol()

export const createReCaptcha = {
  install: app => {
    const { getClient } = useClientStore()
    const sentry = useSentry()

    const { client } = getClient()

    const site_key = client.value.integrations.recaptcha?.site_key
    let is_loaded = false

    const is_recaptcha_processing = ref(false)

    const loadReCaptcha = async () => {
      if (is_loaded) return

      try {
        await loadScript(`https://www.google.com/recaptcha/enterprise.js?render=${site_key}`)

        is_loaded = true
      } catch {
        throw new Error('errors.security_verification_could_not_be_loaded')
      }
    }

    const waitForReCaptchaReady = (timeout = 3000) => new Promise((resolve, reject) => {
      if (!window.grecaptcha || !window.grecaptcha.enterprise)
        return reject(new Error('ReCaptcha not available'))

      const timeoutId = setTimeout(() => {
        reject(new Error('ReCaptcha ready timeout'))
      }, timeout)

      try {
        window.grecaptcha.enterprise.ready(() => {
          clearTimeout(timeoutId)
          resolve()
        })
      } catch (e) {
        clearTimeout(timeoutId)
        reject(e)
      }
    })

    const executeReCaptcha = async action => {
      try {
        is_recaptcha_processing.value = true

        await loadReCaptcha()
        await waitForReCaptchaReady()

        return await window.grecaptcha.enterprise.execute(site_key, { action })
      } catch (e) {
        sentry.error(e)
        throw new Error('errors.an_error_occurred_during_the_security_check')
      } finally {
        is_recaptcha_processing.value = false
      }
    }

    const clearReCaptcha = () => {
      try {
        document.querySelectorAll('script[src*="recaptcha"]')
          .forEach(script => script.remove())

        document.querySelectorAll('[class*="recaptcha"], [id*="recaptcha"], .g-recaptcha, .grecaptcha-badge')
          .forEach(el => el.remove())

        is_loaded = false

        if (window.grecaptcha) delete window.grecaptcha
      } catch (e) {
        sentry.error(e)
      }
    }

    app.provide(recaptchaSymbol, {
      is_recaptcha_processing,
      loadReCaptcha,
      executeReCaptcha,
      clearReCaptcha
    })
  }
}

export const useReCaptcha = () => inject(recaptchaSymbol)